import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Col, Row, Tag, Badge, Divider, Space, Typography } from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  ApartmentOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCareerPath } from "Hooks/AdminHooks/Organization/useCareerPath";

const { Text, Title } = Typography;

const CareerPathDetail = () => {
  const { careerPathId } = useParams();
  const loacation = useLocation();
  const departmentId = loacation.state?.departmentId;

  const navigate = useNavigate();

  const { careerPath, fetchCareerPath, loadingEdit } = useCareerPath();

  const jobTitlesList = careerPath?.jobTitles || [];
  const domainsList = careerPath?.domainKnowledges || [];

  useEffect(() => {
    if (careerPathId) {
      fetchCareerPath(careerPathId);
    }
  }, [careerPathId]);

  const addJobTitle = () => {
    navigate(`/create-jobtitle/${careerPathId}`, {
      state: { returnTo: "career-path-detail", careerPathId, departmentId },
    });
  };

  if (loadingEdit || !careerPath) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: 24, textAlign: "center" }}>Loading Career Path...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={{ padding: 24 }}>
        {/* TOP NAV/HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Space direction="vertical" size={0}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ padding: 0, marginBottom: 8 }}
            >
              Back
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {careerPath.name}
            </Title>
            <Text type="secondary">Public ID: {careerPath.publicId}</Text>
          </Space>
          <Tag
            color={careerPath.isActive ? "green" : "red"}
            style={{ fontSize: "14px", padding: "4px 12px" }}
          >
            {careerPath.isActive ? "Active Path" : "Inactive Path"}
          </Tag>
        </div>

        <Row gutter={[24, 24]}>
          {/* LEFT COLUMN: DEPARTMENT & INFO */}
          <Col xs={24} lg={8}>
            <Card title="Context Details" bordered={false} className="shadow-sm">
              {/* DEPARTMENT SECTION */}
              <div className="mb-3">
                <Space align="start">
                  <ApartmentOutlined style={{ marginTop: 4, color: "#1890ff" }} />
                  <div>
                    <Text strong>Department</Text>
                    <p className="mb-0">{careerPath.department?.name || "N/A"}</p>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {careerPath.department?.description}
                    </Text>
                  </div>
                </Space>
              </div>

              <Divider style={{ margin: "16px 0" }} />

              {/* ADDED: CAREER PATH DETAILS SECTION */}
              <div className="mb-3">
                <Space align="start">
                  <FileTextOutlined style={{ marginTop: 4, color: "#52c41a" }} />
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <Text strong>Career Path ID</Text>
                      <Tag
                        color="blue"
                        className="m-0"
                        style={{ borderRadius: "4px", fontSize: "11px" }}
                      >
                        {careerPath.publicId}
                      </Tag>
                    </div>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", display: "block", marginTop: "4px" }}
                    >
                      {careerPath.description || "No description provided."}
                    </Text>
                  </div>
                </Space>
              </div>

              {/* DOMAINS SECTION */}
              <div className="mb-3">
                <Text strong>Domains</Text>
                <div style={{ marginTop: 8 }}>
                  {domainsList.length > 0 ? (
                    <Space size={[6, 6]} wrap>
                      {domainsList.map((domain) => (
                        <Tag
                          key={domain.domainKnowledgeId}
                          color="gold"
                          style={{ borderRadius: "4px" }}
                        >
                          {domain.name}
                          <span style={{ opacity: 0.6 }}> | {domain.industry}</span>
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      No domains assigned
                    </Text>
                  )}
                </div>
              </div>

              <Divider style={{ margin: "12px 0" }} />

              <div className="mb-2">
                <ClockCircleOutlined style={{ marginRight: 8, color: "#8c8c8c" }} />
                <Text type="secondary">Created: </Text>
                <Text>{new Date(careerPath.createdOn).toLocaleDateString()}</Text>
              </div>
              <div>
                <ClockCircleOutlined style={{ marginRight: 8, color: "#8c8c8c" }} />
                <Text type="secondary">Updated: </Text>
                <Text>{new Date(careerPath.lastModifiedOn).toLocaleDateString()}</Text>
              </div>
            </Card>
          </Col>

          {/* RIGHT COLUMN: JOB TITLES SEQUENCE */}
          <Col xs={24} lg={16}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Title level={4} style={{ margin: 0 }}>
                Growth Sequence{" "}
                <Badge
                  count={jobTitlesList.length}
                  showZero
                  color="#1890ff"
                  style={{ marginLeft: 8 }}
                />
              </Title>
              <Button className="btn-primary" icon={<PlusOutlined />} onClick={addJobTitle}>
                Add Job Title
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              {jobTitlesList.length > 0 ? (
                [...jobTitlesList]
                  .sort((a, b) => b.stepOrder - a.stepOrder)
                  .map((job) => (
                    <Col xs={24} key={job.id}>
                      <Card
                        size="small"
                        className="shadow-sm border-0"
                        style={{ borderLeft: "4px solid #1890ff", padding: "8px" }}
                      >
                        <Row gutter={[16, 12]} align="top">
                          {/* Step Order Column */}
                          <Col xs={4} md={2} className="text-center">
                            <Title level={3} style={{ margin: 0, color: "#e8e8e8" }}>
                              {job.stepOrder}
                            </Title>
                          </Col>

                          {/* Main Content Column */}
                          <Col xs={20} md={14}>
                            <Text strong style={{ fontSize: "16px" }}>
                              {job.name}
                            </Text>
                            <br />
                            <Text
                              type="secondary"
                              style={{ fontSize: "13px", display: "block", marginBottom: "8px" }}
                            >
                              {job.description}
                            </Text>

                            {/* ADDED: SKILLS SECTION */}
                            <div style={{ marginTop: "8px" }}>
                              {job.requiredSkills && job.requiredSkills.length > 0 ? (
                                <Space size={[0, 4]} wrap>
                                  {job.requiredSkills.map((skill) => (
                                    <Tag
                                      key={skill.skillId}
                                      color="default"
                                      style={{
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        background: "#f5f5f5",
                                        border: "1px solid #d9d9d9",
                                      }}
                                    >
                                      {skill.skillName}
                                    </Tag>
                                  ))}
                                </Space>
                              ) : (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "11px", fontStyle: "italic" }}
                                >
                                  No specific skills listed
                                </Text>
                              )}
                            </div>
                          </Col>

                          {/* Metadata Column */}
                          <Col xs={24} md={8} className="text-md-end">
                            <Space direction="vertical" align="end" size={4}>
                              <Tag color="blue" style={{ margin: 0, borderRadius: "4px" }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                Exp: {job.minExperienceRequired}
                                {job.maxExperienceRequired
                                  ? ` - ${job.maxExperienceRequired}`
                                  : "+"}{" "}
                                Yrs
                              </Tag>
                              <Text type="secondary" style={{ fontSize: "11px" }}>
                                {job.publicId}
                              </Text>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))
              ) : (
                <Col span={24}>
                  <Card className="text-center py-4">
                    <Text type="secondary">
                      No job titles have been added to this career path yet.
                    </Text>
                  </Card>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </div>
    </DashboardLayout>
  );
};

export default CareerPathDetail;
